import * as React from 'react';

import { hasAccessibleName } from '../icons/hasA11yProp';
import type { IconSize, TextIconSize } from '../icons/iconSlot';
import { STATUS_ICONS, type StatusIconStatus, type StatusIconVariant } from './statusIcons';

export type StatusIconProps = {
    status: StatusIconStatus;
    /** `filled` cuts the glyph out of a solid shape, for emphasis: on a colored background, as a row's main status. */
    variant?: StatusIconVariant;
    /** In px from the scale, 24 by default, or `1em` in a line of text; slots that size icons override it. */
    size?: IconSize | TextIconSize;
    className?: string;
    /** The escape hatch: its `width` and `height` set another size, its `color` another color. */
    style?: React.CSSProperties;
    role?: string;
} & React.AriaAttributes &
    Omit<React.DOMAttributes<SVGSVGElement>, 'children' | 'dangerouslySetInnerHTML'>;

/** Hidden from screen readers until named (a Tooltip around it names it): stricter than Lucide's `hasA11yProp`. */
export const StatusIcon = React.forwardRef<SVGSVGElement, StatusIconProps>(function StatusIcon(
    { status, variant = 'outline', size = 24, style, ...props },
    ref,
) {
    const Glyph = STATUS_ICONS[status][variant];
    const isNamed = hasAccessibleName(props);
    return (
        <Glyph
            ref={ref}
            role={isNamed ? 'img' : undefined}
            aria-hidden={isNamed ? undefined : true}
            {...props}
            size={size}
            // Painted in the CSS color (the glyph's stroke is currentColor), so `style` can set another one.
            style={{ color: STATUS_ICONS[status].color, ...style }}
        />
    );
});
